// apps/web/src/hooks/voice/use-voice-cdrs.ts
'use client';

import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useVoiceSocket } from '@/hooks/use-voice-socket';
import type { VoiceCdr, VoiceCdrFilter, VoiceCdrListMeta, VoiceCdrStats } from './types';

export interface CdrStatsByPoint {
  key: string;
  total: number;
  answered: number;
  missed: number;
  // group_by=queue (motor 22/07): abandono de fila por queue_name.
  abandoned: number;
  avg_duration: number;
  // group_by=agent (motor 22/07): TMA real (média de billsec das atendidas).
  avg_billsec?: number;
  // Motor (10/06): com group_by=agent o bucket agora traz agent_name. Sem
  // isso a UI tinha que fazer lookup secundário pra resolver o id no chart.
  agent_name?: string | null;
  [k: string]: unknown;
}

export function useCdrs(filter: VoiceCdrFilter) {
  const [data, setData] = useState<VoiceCdr[]>([]);
  const [meta, setMeta] = useState<VoiceCdrListMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams(
        Object.entries(filter)
          .filter(([, v]) => v !== undefined && v !== '')
          .map(([k, v]) => [k, String(v)]),
      ).toString();
      const path = `/telefonia/historico${qs ? `?${qs}` : ''}`;
      // Motor retorna { cdrs: [...], meta: { total, page, per_page } }.
      // Unwrap aqui; meta fica null se algum motor antigo mandar array cru.
      const res = await api.request<
        VoiceCdr[] | { cdrs: VoiceCdr[]; meta?: VoiceCdrListMeta }
      >(path);
      if (Array.isArray(res)) {
        setData(res);
        setMeta(null);
      } else {
        setData(res?.cdrs ?? []);
        setMeta(res?.meta ?? null);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { refetch(); }, [refetch]);

  // Atualiza row in-place quando voice.recording.available chega — sem refetch
  // de página inteira. Toast Linear-style avisando.
  useVoiceSocket((evt) => {
    if (evt.event !== 'voice.recording.available') return;
    const d = evt.data as { cdr_id?: number; unique_id?: string; recording_path?: string };
    setData((prev) =>
      prev.map((c) => {
        const match = (d.cdr_id && c.id === d.cdr_id) || (d.unique_id && c.unique_id === d.unique_id);
        if (!match) return c;
        return { ...c, recording_available: true, recording_path: d.recording_path ?? c.recording_path };
      }),
    );
    toast.success('Gravação disponível', { duration: 3000 });
  });

  return { data, meta, loading, error, refetch };
}

export function useCdrStats(
  filter: {
    from?: string;
    to?: string;
    direction?: string;
    disposition?: string;
    agent_id?: number;
  } = {},
) {
  const [data, setData] = useState<VoiceCdrStats | null>(null);
  // direction/disposition: os cards do Histórico eram all-time e ignoravam os
  // filtros ativos da página (auditoria 22/07) — motor honra os dois agora.
  const { from, to, direction, disposition, agent_id } = filter;
  useEffect(() => {
    // cancelled: mudança rápida de filtro fazia a resposta ANTIGA resolver por
    // último e sobrescrever a nova (cards com dado do filtro anterior — review 23/07).
    let cancelled = false;
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    if (direction) qs.set('direction', direction);
    if (disposition) qs.set('disposition', disposition);
    // O backend já repassa agent_id ao motor (voice.controller.ts:1331-1338) e o
    // motor honra no base_scope do /cdrs/stats — só o front não mandava.
    if (agent_id) qs.set('agent_id', String(agent_id));
    const path = qs.toString()
      ? `/telefonia/historico/stats?${qs.toString()}`
      : '/telefonia/historico/stats';
    api.request<VoiceCdrStats>(path)
      .then((d) => !cancelled && setData(d))
      .catch(() => !cancelled && setData(null));
    return () => {
      cancelled = true;
    };
  }, [from, to, direction, disposition, agent_id]);
  return data;
}

// Export CSV: busca TODAS as páginas do conjunto filtrado. O botão exportava só
// a página visível (máx 50 linhas) sem nenhum aviso de truncamento (auditoria
// 22/07). Cap de segurança bem acima de qualquer export real.
export const CSV_EXPORT_MAX_ROWS = 10000;

export async function fetchAllCdrs(filter: VoiceCdrFilter): Promise<VoiceCdr[]> {
  const all: VoiceCdr[] = [];
  const perPage = 100;
  let offset = 0;
  for (;;) {
    const qs = new URLSearchParams(
      Object.entries({ ...filter, limit: perPage, offset })
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => [k, String(v)]),
    ).toString();
    const res = await api.request<VoiceCdr[] | { cdrs: VoiceCdr[]; meta?: VoiceCdrListMeta }>(
      `/telefonia/historico?${qs}`,
    );
    const page = Array.isArray(res) ? res : (res?.cdrs ?? []);
    const total = Array.isArray(res) ? null : (res?.meta?.total ?? null);
    all.push(...page);
    if (page.length < perPage || (total != null && all.length >= total)) break;
    if (all.length >= CSV_EXPORT_MAX_ROWS) break;
    offset += perPage;
  }
  return all;
}

export function useCdrStatsBy(
  groupBy: 'hour' | 'day' | 'agent' | 'queue' | 'disposition',
  filter: { from?: string; to?: string } = {},
) {
  // Estado keyed pelo filtro: `loading` é DERIVADO (key gravada ≠ key atual) —
  // sem setLoading(true) síncrono no effect (regra react-hooks/set-state-in-effect)
  // e imune a resposta fora de ordem (só o fetch da key atual grava — review 23/07).
  const [state, setState] = useState<{ key: string; data: CdrStatsByPoint[] }>({
    key: '__init__',
    data: [],
  });
  const key = `${groupBy}|${filter.from ?? ''}|${filter.to ?? ''}`;

  useEffect(() => {
    let cancelled = false;
    const qs = new URLSearchParams({ group_by: groupBy });
    if (filter.from) qs.set('from', filter.from);
    if (filter.to) qs.set('to', filter.to);
    // tz do browser: buckets day/hour no fuso do usuário. Sem isso o motor
    // agrupava em UTC e o gráfico deslocava 3h (pico 14h aparecia 17h) e
    // chamadas 21h-23h59 BRT contavam no dia SEGUINTE (auditoria 22/07).
    if (groupBy === 'day' || groupBy === 'hour') {
      qs.set('tz', Intl.DateTimeFormat().resolvedOptions().timeZone);
    }
    api
      .request<
        | { items: CdrStatsByPoint[] }
        | Array<{
            bucket: string | number;
            total: number;
            answered?: number;
            missed?: number;
            abandoned?: number;
            avg_duration?: number;
            avg_billsec?: number;
            agent_name?: string | null;
          }>
      >(`/telefonia/historico/stats/by?${qs}`)
      .then((r) => {
        const raw = Array.isArray(r) ? r : ((r as { items: CdrStatsByPoint[] }).items ?? []);
        // Motor retorna `bucket` como nome do grupo (hora/dia/agente/fila/disposicao).
        // group_by=agent traz number (assignee_id); demais groupBys trazem string.
        // Frontend usa `key` (string) consistentemente — normaliza aqui.
        const items: CdrStatsByPoint[] = raw.map((it) => {
          const obj = it as {
            bucket?: string | number;
            key?: string;
            agent_name?: string | null;
            [k: string]: unknown;
          };
          const bucket = obj.bucket;
          return {
            ...(obj as Record<string, unknown>),
            key: obj.key ?? (bucket !== undefined && bucket !== null ? String(bucket) : ''),
            total: (obj.total as number) ?? 0,
            answered: (obj.answered as number) ?? 0,
            missed: (obj.missed as number) ?? 0,
            abandoned: (obj.abandoned as number) ?? 0,
            avg_duration: (obj.avg_duration as number) ?? 0,
          } as CdrStatsByPoint;
        });
        if (!cancelled) setState({ key, data: items });
      })
      .catch(() => !cancelled && setState({ key, data: [] }));
    return () => {
      cancelled = true;
    };
  }, [key, groupBy, filter.from, filter.to]);

  return { data: state.data, loading: state.key !== key };
}
